import { EventEmitter, Inject, Injectable, effect, inject, signal, Optional } from '@angular/core';
import { GPKG_AUTO_STYLE_HANDLER, GpkgAutoStyleHandler, GpkgAutoStyleType } from '../handlers';
import { LayerDetails, OptionalLayer, SeedableLayerDetails } from '../types';
import { MapStatusService } from './map-status.service';
import { TransformationService } from './transformation.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { WMS_CONFIG } from '../../core/tokens';
import { LatLng, LatLngBounds, Layer } from 'leaflet';
import { XMLParser } from 'fast-xml-parser';
import { StorageService } from '../../services/storage/storage.service';
import { GeoPackage, GeoPackageAPI, setSqljsWasmLocateFile } from '@ngageoint/geopackage';
// @ts-ignore - shapefile für robusteres Parsen
import * as shapefile from 'shapefile';
// @ts-ignore - shpjs für ZIP-Shapefile-Dateien
import shp from 'shpjs';
// @ts-ignore - libarchive-wasm für 7z-Archive
import { ArchiveReader, libarchiveWasm } from 'libarchive-wasm';
import { PROJECT_PROVIDER, ProjectProvider } from '../../core';
import { firstValueFrom } from 'rxjs';
import { FileExportService } from '../../services/file-export.service';
import { OfflineModeService } from '../../services/offline-mode.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GpkgLayerSelectModalComponent, GpkgLayerInfo, GpkgGeometryType } from '../controls/gpkg-layer-select-modal.component';

export class WmsLayerData {
	name!: string;
	title!: string;
	format!: string;
	abstract!: string;
	bbox?: LatLngBounds;
	transparent!: boolean;
}

export type WmsRegion = 'global' | 'DE' | 'BB' | 'MV';

export type WmsAuthType = 'none' | 'basic';

export class WmsData {
	name!: string;
	url!: string;
	formats?: string[];
	layers?: WmsLayerData[];
	fallbackLayers?: WmsLayerData[];  // Vordefinierte Layer als Fallback wenn GetCapabilities fehlschlägt (CORS)
	maxNativeZoom?: number;  // Maximale native Zoomstufe - darüber wird hochskaliert
	minZoom?: number;  // Minimale Zoomstufe - darunter wird Layer nicht angezeigt
	region?: WmsRegion;  // Verfügbarkeitsregion für Filterung
	auth?: WmsAuthType;  // Authentifizierungstyp (default: 'none')
	credentials?: string;  // Verschleierte Credentials (Format: "username:password" obfuskiert)
	crs?: string;  // CRS für WMS-Anfragen (z.B. 'EPSG:25833'), default: 'EPSG:4326'
}

/**
 * Hilfsfunktionen für WMS-Credentials-Verschleierung.
 * HINWEIS: Dies ist keine echte Verschlüsselung, nur Obfuskation!
 */
const WMS_OBFUSCATION_KEY = 'L7x9Kp2mN4qR8sT1';

export function decodeWmsCredentials(encoded: string): { username: string; password: string } | null {
	if (!encoded) return null;
	try {
		// Base64-URL-safe decode (ersetze _ und - zurück)
		const base64 = encoded.replace(/_/g, '/').replace(/-/g, '+');
		const decoded = atob(base64);
		// XOR mit Key
		const keyChars = WMS_OBFUSCATION_KEY.split('');
		const decrypted = decoded.split('').map((char, i) => {
			const keyChar = keyChars[i % keyChars.length];
			return String.fromCharCode(char.charCodeAt(0) ^ keyChar.charCodeAt(0));
		}).join('');
		// Split username:password
		const colonIndex = decrypted.indexOf(':');
		if (colonIndex === -1) return null;
		return {
			username: decrypted.substring(0, colonIndex),
			password: decrypted.substring(colonIndex + 1)
		};
	} catch {
		return null;
	}
}

export function getWmsBasicAuthHeader(wms: WmsData): string | null {
	if (wms.auth !== 'basic' || !wms.credentials) return null;
	const creds = decodeWmsCredentials(wms.credentials);
	if (!creds) return null;
	// UTF-8 safe base64 encoding (btoa kann nur Latin-1)
	const authString = `${creds.username}:${creds.password}`;
	const utf8Bytes = new TextEncoder().encode(authString);
	const base64 = btoa(String.fromCharCode(...utf8Bytes));
	return 'Basic ' + base64;
}

export class MapDefinition {
	layers!: LayerDetails[];
}

export type RegionType = 'bounds' | 'polygon';

export interface Region {
	type: RegionType;
	bounds: LatLngBounds;           // Für 'bounds' die Region, für 'polygon' die Bounding Box
	polygon?: LatLng[];             // Nur für 'polygon': Array der Eckpunkte
}

export interface Regions {
	regions: Region[];
}

@Injectable({
	providedIn: 'root'
})
export class LayerService {
	public layersChanged = new EventEmitter<void>();
	public layerVisibilityChanged = new EventEmitter<void>();
	public layerOrderChanged = new EventEmitter<string[]>();  // Nur Z-Order-Änderung, kein Rebuild - übergibt die neue Reihenfolge
	public layersDefined = new EventEmitter<void>();
	public offlineModeChanged = new EventEmitter<boolean>();
	public attachmentsChanged = new EventEmitter<void>();
	
	public boundsFromMapRequested = new EventEmitter<void>();
	public customBoundsFromMapRequested = new EventEmitter<void>();
	public polygonFromMapRequested = new EventEmitter<void>();
	public freehandFromMapRequested = new EventEmitter<void>();
	public geoJsonImportRequested = new EventEmitter<File[]>();
	public regionDrawingCompleted = new EventEmitter<void>();  // Nach Bereichszeichnung Modal wieder öffnen
	public regionsChanged = new EventEmitter<void>();
	public regionNamesChanged = new EventEmitter<void>();
	public regionClicked = new EventEmitter<LatLngBounds>();
	public zoomToLayerRequested = new EventEmitter<string>();

	// Events für Projekt-Punkte
	public pointBoundsSelectionRequested = new EventEmitter<void>();
	public pointsSelectedFromBounds = new EventEmitter<string[]>();
	public projectPointsChanged = new EventEmitter<Set<string>>();
	public assignedPointsChanged = new EventEmitter<Set<string>>();

	// Events für Zeichnungslayer
	public zeichnungslayerEditRequested = new EventEmitter<string | null>();  // Layer-ID oder null zum Beenden
	public zeichnungslayerChanged = new EventEmitter<void>();  // Bei Änderungen an Zeichnungslayer-Daten (für UI-Aktualisierung)
	public zeichnungslayerReloadRequested = new EventEmitter<void>();  // Komplettes Neuladen der Zeichnungslayer (z.B. nach Refresh)
	public zeichnungslayerVisibilityChanged = new EventEmitter<string>();  // Layer-ID bei Sichtbarkeitsänderung
	public zeichnungslayerZoomRequested = new EventEmitter<string>();  // Zoom zu Layer-ID
	public activeDrawLayerId = signal<string | null>(null);  // Aktiver Zeichnungslayer für Bearbeitung
	public lastActiveDrawLayerId = signal<string | null>(null);  // Zuletzt aktiver Zeichnungslayer (persistiert)
	public isDrawToolActive = signal<boolean>(false);  // True wenn ein Zeichentool aktiv ist

	// Events für GPKG-Layer
	public gpkgLayerZoomRequested = new EventEmitter<string>();  // Zoom zu GPKG-Layer (fullLayerName)

	// Events für Grid-Layer
	public gridZoomRequested = new EventEmitter<void>();  // Zoom zum Punktraster

	// Events für Punkte-Layer
	public pointsLayerVisibilityChanged = new EventEmitter<boolean>();  // true = sichtbar
	// Events für Flächen-Layer
	public polygonsLayerVisibilityChanged = new EventEmitter<boolean>();  // true = sichtbar

	private storage: StorageService = inject(StorageService);
	public pointGridIdx = -1;

	/**
	 * Layer-Cache für schnellere Wiederherstellung beim Navigieren.
	 * Speichert Leaflet-Layer-Instanzen nach Namen.
	 */
	private layerCache = new Map<string, Layer>();
	private layerCacheProject: string | null = null;  // Projekt für das der Cache gilt

	/**
	 * Gibt einen gecachten Layer zurück oder undefined wenn nicht vorhanden.
	 */
	getCachedLayer(name: string): Layer | undefined {
		// Cache nur gültig für aktuelles Projekt
		if (this.layerCacheProject !== this.ps.project()) {
			this.clearLayerCache();
			return undefined;
		}
		return this.layerCache.get(name);
	}

	/**
	 * Speichert einen Layer im Cache.
	 */
	cacheLayer(name: string, layer: Layer): void {
		this.layerCacheProject = this.ps.project();
		this.layerCache.set(name, layer);
	}

	/**
	 * Entfernt einen Layer aus dem Cache.
	 */
	uncacheLayer(name: string): void {
		this.layerCache.delete(name);
	}

	/**
	 * Leert den gesamten Layer-Cache.
	 */
	clearLayerCache(): void {
		this.layerCache.clear();
		this.layerCacheProject = null;
	}

	/**
	 * Prüft ob ein Layer im Cache ist.
	 */
	hasLayerCached(name: string): boolean {
		if (this.layerCacheProject !== this.ps.project()) {
			return false;
		}
		return this.layerCache.has(name);
	}

	globalMapDefinition: MapDefinition;
	mapDef = signal<MapDefinition>({ layers: [] } as MapDefinition);
	regions = signal<Regions>({ regions: [] } as Regions);
	private hasZoomedToRegion = false;  // Verhindert mehrfaches Zoomen beim Laden
	private lastProjectLoaded: string | null = null;  // Verhindert mehrfaches Laden desselben Projekts

	pointGrid: () => LayerDetails;

	private fileExport: FileExportService = inject(FileExportService);
	private gpkgAutoStyleHandler: GpkgAutoStyleHandler | null = inject(GPKG_AUTO_STYLE_HANDLER, { optional: true });

	public wmsServer: WmsData[];
	public wmsServerMap = new Map<string, WmsData>();

	constructor(
		private _http: HttpClient,
		private ms: MapStatusService,
		@Inject(PROJECT_PROVIDER) private ps: ProjectProvider,
		private ts: TransformationService,
		private oms: OfflineModeService,
		private modalService: NgbModal,
		@Inject(WMS_CONFIG) wmsConfig: WmsData[]
	) {
		this.wmsServer = wmsConfig;
		setSqljsWasmLocateFile((filename: string) => `/assets/leaflet/${filename}`);

		this.globalMapDefinition = { layers: [
			{
				type: "osm",
				isBase: true,
				display: true,
				url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
				name: "OpenStreetMap",
				minZoom: this.ms.mapLimits.minZoom,
				maxZoom: this.ms.mapLimits.maxZoom,
				transparent: false,
				bbox: ms.mapLimits.maxSubBounds
			} as SeedableLayerDetails
		]};
		this.wmsServer.forEach(s => this.wmsServerMap.set(s.name, s));
		this.pointGrid = () => {
			return {
				type: 'grid',
				isBase: false,
				resolution: 5,  // 1 Punkt pro 5 Hektar
				url: '',
				name: 'Punktraster',
				minZoom: this.ms.mapLimits.minZoom,
				maxZoom: this.ms.mapLimits.maxZoom,
				display: false,
				bbox: ms.mapLimits.maxSubBounds,
				gridStyle: {
					color: '#3388ff',
					fillColor: '#3388ff',
					fillOpacity: 0.5,
					radius: 3
				}
			} as LayerDetails;
		};
		
		effect(() => {
			// Signals lesen um Effect zu registrieren
			const _offline = this.oms.offlineMode();
			const currentProject = this.ps.project();

			// Nur bei echtem Projektwechsel neu laden
			if (currentProject === this.lastProjectLoaded) {
				return;
			}
			this.lastProjectLoaded = currentProject;

			if (currentProject) {
				// Signal-Wert VOR async Calls erfassen (vermeidet "consumer out of bounds" Fehler)
				const isRestoring = this.ps.isRestoringProject();

				// Beide Werte parallel laden, dann einmal layersChanged emittieren
				Promise.all([
					this.ps.getProjectValue("mapDefinition"),
					this.ps.getProjectValue("regions")
				]).then(([md, r]: [any, any]) => {
					// MapDefinition setzen
					if (md) {
						this.mapDef.set(md);
					} else {
						this.mapDef.set(this.globalMapDefinition);
						this.ps.setProjectValue("mapDefinition", this.mapDef(), 'keyvalue');
					}

					// Regions setzen
					if (r?.regions) {
						// Migration: Alte LatLngBounds[] zu neuer Region[] Struktur
						r.regions = r.regions.map((item: any) => {
							if (item.type) {
								// Neues Format: Region-Objekt
								const region: Region = {
									type: item.type,
									bounds: new LatLngBounds(item.bounds._southWest, item.bounds._northEast)
								};
								if (item.polygon) {
									region.polygon = item.polygon.map((p: any) => new LatLng(p.lat, p.lng));
								}
								return region;
							} else {
								// Altes Format: Direktes LatLngBounds → zu Region konvertieren
								return {
									type: 'bounds',
									bounds: new LatLngBounds(item._southWest, item._northEast)
								} as Region;
							}
						});
					}
					if (r) {
						this.regions.set(r);
						if (r.regions?.length > 0) {
							// regionsChanged wird NICHT emittiert, da layersChanged folgt
							// und _doLayersChanged() selbst onRegionsChanged() aufruft
							// Nur zum Projektbereich zoomen, wenn kein Restore und noch nicht gezoomt
							if (!isRestoring && !this.hasZoomedToRegion) {
								this.hasZoomedToRegion = true;
								const combined = r.regions.reduce(
									(acc: LatLngBounds, region: Region) => acc.extend(region.bounds),
									new LatLngBounds(r.regions[0].bounds.getSouthWest(), r.regions[0].bounds.getNorthEast())
								);
								this.regionClicked.emit(combined);
							}
						}
					} else {
						this.regions.set({regions: []});
						this.ps.setProjectValue("regions", this.regions(), 'keyvalue');
					}

					// Nur einmal emittieren nachdem alles geladen ist
					this.layersChanged.emit();
					this.layersDefined.emit();
				});
			}
			else {
				this.mapDef.set(this.globalMapDefinition);
				this.regions.set({regions: []});
				this.hasZoomedToRegion = false;  // Reset für nächstes Projekt
				this.layersChanged.emit();
				this.layersDefined.emit();
			}
		});
		
	}

	async hiddenLayers(): Promise<string[]> {
		const hidden = await this.ps.getProjectValue('hiddenLayers') as string[];
		// Filter null/undefined-Werte heraus (können durch alte Daten entstehen)
		return (hidden || []).filter(h => !!h);
	}
	
	async toggleVisibility(ld: LayerDetails, sublayer?: string) {
		const hidden = await this.hiddenLayers();
		// GPKG-Sublayer: Voller Key "dateiname_sublayer"
		const key = sublayer ? `${ld.name}_${sublayer}` : ld.name;
		const wasHidden = hidden.includes(key);
		if (wasHidden) {
			const index = hidden.indexOf(key);
			if (index > -1)
				hidden.splice(index, 1);
		} else
			hidden.push(key);
		await this.ps.setProjectValue('hiddenLayers', hidden);

		// WMS-Layer: layersChanged emittieren damit Layer neu erstellt/entfernt wird
		// (WMS-Layer werden nicht erstellt wenn sie versteckt sind, um Bandbreite zu sparen)
		if (ld.type === 'wms') {
			this.layersChanged.emit();
		} else {
			this.layerVisibilityChanged.emit();
		}
	}
	
	numberOfLayers() {
		const numOfLayers = this.mapDef()?.layers.reduce(
			(num: number, ld: LayerDetails) => {
				if (ld.layers) {
					return num + ld.layers.reduce((sum, layer) => {
						return sum + (layer.enabled ? 1 : 0);
					}, 0);
				}
				else {
					return num + (ld.display || ld.type === 'geotiff' ? 1 : 0);
				}
			}, 0);
		return numOfLayers ?? 0;
	}
	
	serverNames() {
		return this.wmsServer.map((d: WmsData) => d.name);
	}
	
	isFullyTransparent(ld: LayerDetails): boolean {
		if (!ld.layers) return true;
		if (ld.layers.find((l: OptionalLayer) => l.enabled && !l.transparent)) return false;
		return true;
	}

	private _parseWmsCapabilities(wms: WmsData, data: string): WmsData {
		const parser = new XMLParser({ ignoreAttributes: false, removeNSPrefix: true });
		const obj = parser.parse(data);
		const wmscl = obj.WMS_Capabilities;
		const wmtcl = obj.WMT_MS_Capabilities;
		const c = wmscl ? wmscl : wmtcl;
		if (!c?.Capability?.Layer) {
			console.warn(`WMS "${wms.name}": Keine Layer in Capabilities gefunden`);
			return wms;
		}
		const formats = c.Capability?.Request?.GetMap?.Format;
		const rootLayer = c?.Capability?.Layer;
		const layers = rootLayer?.Layer ?? (rootLayer?.Name ? rootLayer : undefined);
		if (!wms.layers) wms.layers = new Array<WmsLayerData>();
		if (!wms.formats) wms.formats = new Array<string>();
		const addLayer = (l: any) => {
			// Verschachtelte Layer rekursiv auflösen (z.B. MV Naturraumkarte)
			if (l.Layer) {
				const children = Array.isArray(l.Layer) ? l.Layer : [l.Layer];
				children.forEach(addLayer);
				return;
			}
			const myL: WmsLayerData = { name: l.Name, title: l.Title, format: 'application/json;type=geojson', abstract: l.Abstract, transparent: l['@_opaque'] === '0' };
			// WMS 1.3.0 Format
			if (l.EX_GeographicBoundingBox) {
				myL.bbox = new LatLngBounds(
					new LatLng(l.EX_GeographicBoundingBox.southBoundLatitude, l.EX_GeographicBoundingBox.westBoundLongitude),
					new LatLng(l.EX_GeographicBoundingBox.northBoundLatitude, l.EX_GeographicBoundingBox.eastBoundLongitude)
				);
			}
			// WMS 1.1.1 Format (LatLonBoundingBox)
			else if (l.LatLonBoundingBox) {
				const bb = l.LatLonBoundingBox;
				myL.bbox = new LatLngBounds(
					new LatLng(parseFloat(bb['@_miny']), parseFloat(bb['@_minx'])),
					new LatLng(parseFloat(bb['@_maxy']), parseFloat(bb['@_maxx']))
				);
			}
			// BoundingBox mit EPSG:4326 oder CRS:84
			else if (l.BoundingBox) {
				const bboxes = Array.isArray(l.BoundingBox) ? l.BoundingBox : [l.BoundingBox];
				const geoBbox = bboxes.find((bb: any) =>
					bb['@_CRS'] === 'EPSG:4326' || bb['@_CRS'] === 'CRS:84' ||
					bb['@_SRS'] === 'EPSG:4326' || bb['@_SRS'] === 'CRS:84'
				);
				if (geoBbox) {
					myL.bbox = new LatLngBounds(
						new LatLng(parseFloat(geoBbox['@_miny']), parseFloat(geoBbox['@_minx'])),
						new LatLng(parseFloat(geoBbox['@_maxy']), parseFloat(geoBbox['@_maxx']))
					);
				}
			}
			wms.layers?.push(myL);
		};
		const addFormat = (f: any) => wms.formats?.push(f);
		if (layers instanceof Array) layers.forEach(addLayer);
		else if (layers) addLayer(layers);
		if (formats instanceof Array) formats.forEach(addFormat);
		else if (formats) addFormat(formats);
		return wms;	
	}
	
	public resolveWmsLayersWithCallback(name: string, success: (data: WmsLayerData[]) => void, failed?: (error?: any) => void) {
		const wms = this.wmsServerMap.get(name);
		if (!wms) {
			if (failed) failed();
			return;
		}
		if (wms && wms.layers && wms.layers?.length > 0) {
			success(wms.layers);
			return;
		}
		this.loadWmsData(wms).subscribe({
			next: (data) => {
				try {
					this._parseWmsCapabilities(wms, data);
					success(wms.layers!);
				} catch (err) {
					console.warn(`WMS-Capabilities für "${name}" konnten nicht verarbeitet werden:`, err);
					if (wms.fallbackLayers?.length) {
						console.warn(`→ Verwende ${wms.fallbackLayers.length} Fallback-Layer für "${name}"`);
						wms.layers = wms.fallbackLayers;
						success(wms.layers);
					} else if (failed) {
						failed(err);
					}
				}
			},
			error: (error: Error) => {
				if (wms.fallbackLayers?.length) {
					console.warn(`GetCapabilities für "${name}" fehlgeschlagen (${error.message}), verwende ${wms.fallbackLayers.length} Fallback-Layer`);
					wms.layers = wms.fallbackLayers;
					success(wms.layers);
				} else if (failed) {
					failed(error);
				}
			}
		});
	}

	public async resolveWmsLayers(name: string): Promise<WmsLayerData[]> {
		const wms = this.wmsServerMap.get(name);
		if (!wms) return [];
		if (wms && wms.layers && wms.layers?.length > 0) {
			return wms.layers;
		}
		try {
			const data = await firstValueFrom(this.loadWmsData(wms));
			this._parseWmsCapabilities(wms, data);
		} catch (err) {
			console.warn(`WMS-Capabilities für "${name}" konnten nicht geladen werden:`, err);
			if (wms.fallbackLayers?.length) {
				console.warn(`→ Verwende ${wms.fallbackLayers.length} Fallback-Layer für "${name}"`);
				wms.layers = wms.fallbackLayers;
				return wms.layers;
			}
			return [];
		}
		return wms.layers ?? [];
	}
	
	private loadWmsData(wms: WmsData) {
		const params = new HttpParams({ fromString: 'SERVICE=WMS&REQUEST=GetCapabilities' });
		let headers = new HttpHeaders({ 'content-type': 'text/plain' });

		// Basic Auth Header hinzufügen falls konfiguriert
		const authHeader = getWmsBasicAuthHeader(wms);
		if (authHeader) {
			headers = headers.set('Authorization', authHeader);
		}

		return this._http.request('GET', wms.url, { headers: headers, responseType: 'text', params: params });
	}

	addAttachment(file: File, mapDef: MapDefinition, companionFiles?: File[], storageName?: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const fr = new FileReader();
			fr.readAsArrayBuffer(file);
			fr.onload = async (ev: Event) => {
				if (!ev.target) {
					resolve();
					return;
				}
				const reader = ev.target as FileReader;
				try {
					await this._handleNewAttachment(storageName || file.name, reader.result! as ArrayBuffer, companionFiles, file.name);
					this.attachmentsChanged.emit();
					resolve();
				} catch (err) {
					console.error('Fehler beim Speichern des Attachments:', err);
					reject(err);
				}
			};
			fr.onerror = () => {
				reject(new Error('Fehler beim Lesen der Datei'));
			};
		});
	}

	/**
	 * Öffnet einen File-Picker und akzeptiert nur Dateien mit exakt dem angegebenen Namen.
	 * Wird verwendet um fehlende Geodatendateien wiederherzustellen.
	 * @param expectedFilename Der exakte Dateiname der erwartet wird
	 * @returns Promise<boolean> true wenn Datei erfolgreich hinzugefügt wurde
	 */
	async addMissingAttachment(expectedFilename: string): Promise<boolean> {
		return new Promise((resolve) => {
			const input = document.createElement('input');
			input.type = 'file';
			input.style.display = 'none';
			document.body.appendChild(input);

			const cleanup = () => {
				if (input.parentNode) {
					document.body.removeChild(input);
				}
			};

			input.onchange = async (event: Event) => {
				const target = event.target as HTMLInputElement;
				if (!target.files || target.files.length === 0) {
					cleanup();
					resolve(false);
					return;
				}

				const selectedFile = target.files[0];

				// Namens-Prüfung: exakt oder Shapefile-Quellformat (.zip/.shp/.7z → .geojson)
				const expectedBase = expectedFilename.replace(/\.[^.]+$/, '');
				const selectedBase = selectedFile.name.replace(/\.[^.]+$/, '');
				const selectedExt = selectedFile.name.toLowerCase().split('.').pop() || '';
				const isShapefileSource = ['zip', 'shp', '7z'].includes(selectedExt) && expectedFilename.toLowerCase().endsWith('.geojson');
				if (selectedFile.name !== expectedFilename && !(isShapefileSource && selectedBase === expectedBase)) {
					alert(`Falscher Dateiname!\n\nErwartet: ${expectedFilename}\nAusgewählt: ${selectedFile.name}`);
					cleanup();
					resolve(false);
					return;
				}

				// Datei verarbeiten (Storage-Name = expectedFilename, Typ-Erkennung über Original)
				const fr = new FileReader();
				fr.readAsArrayBuffer(selectedFile);
				fr.onload = async () => {
					await this._handleNewAttachment(expectedFilename, fr.result as ArrayBuffer, undefined, selectedFile.name);
					this.attachmentsChanged.emit();
					cleanup();
					resolve(true);
				};
				fr.onerror = () => {
					cleanup();
					resolve(false);
				};
			};

			// Fallback wenn Dialog abgebrochen wird
			const handleFocus = () => {
				window.removeEventListener('focus', handleFocus);
				// Kurze Verzögerung um onchange Zeit zu geben
				setTimeout(() => {
					if (input.parentNode && !input.files?.length) {
						cleanup();
						resolve(false);
					}
				}, 300);
			};
			window.addEventListener('focus', handleFocus);

			input.click();
		});
	}

	private async _handleNewAttachment(name: string, buf: ArrayBuffer, companionFiles?: File[], originalFileName?: string) {
		// Typ-Erkennung über originalen Dateinamen (z.B. .zip), Storage über name (z.B. .geojson)
		const lower = (originalFileName || name).toLowerCase();
		if (lower.endsWith('.gpkg')) {
			const pkg = await GeoPackageAPI.open(Buffer.from(buf));
			const features = pkg.getFeatureTables();
			const tiles = pkg.getTileTables();

			// Layer-Auswahl-Dialog anzeigen (mit Geometrietypen)
			const selectedLayers = await this._showGpkgLayerSelectModal(name, pkg, features, tiles);
			if (!selectedLayers || selectedLayers.length === 0) {
				// Benutzer hat abgebrochen oder keine Layer ausgewählt
				pkg.close();
				return;
			}

			// Nur ausgewählte Layer in den Metadaten speichern
			const selectedFeatures = selectedLayers.filter(l => l.type === 'feature').map(l => l.name);
			const selectedTiles = selectedLayers.filter(l => l.type === 'tile').map(l => l.name);

			await this.ps.setProjectValue(name, buf, 'attachments');
			await this.ps.setProjectValue(name, {features: selectedFeatures, tiles: selectedTiles}, 'attachments_meta');
			await this.ps.addGeodataFile(name);
			pkg.close();
		}
		else if (lower.endsWith('.tif') || lower.endsWith('.tiff')) {
			await this.ps.setProjectValue(name, buf, 'attachments');
			await this.ps.addGeodataFile(name);
		}
		else if (lower.endsWith('.geojson') || lower.endsWith('.json')) {
			// GeoJSON als Text speichern (nicht als ArrayBuffer)
			const textContent = new TextDecoder().decode(buf);
			try {
				const geojson = JSON.parse(textContent);
				// Validierung: muss type "FeatureCollection", "Feature" oder "GeometryCollection" haben
				if (!geojson.type || !['FeatureCollection', 'Feature', 'GeometryCollection'].includes(geojson.type)) {
					console.error('Ungültiges GeoJSON-Format');
					return;
				}
				await this.ps.setProjectValue(name, textContent, 'attachments');
				await this.ps.addGeodataFile(name);
			} catch (e) {
				console.error('GeoJSON-Parsing fehlgeschlagen:', e);
				return;
			}
		}
		else if (lower.endsWith('.shp')) {
			// Einzelne .shp mit Companion-Dateien
			await this._handleShapefileWithCompanions(name, buf, companionFiles, originalFileName);
		}
		else if (lower.endsWith('.zip')) {
			// ZIP-Datei mit shpjs verarbeiten (enthält .shp/.dbf/.prj)
			await this._handleShapefileZip(name, buf);
		}
		else if (lower.endsWith('.7z')) {
			// 7z-Datei mit Shapefile-Inhalt verarbeiten
			await this._handleShapefile7z(name, buf);
		}
	}

	/**
	 * Zeigt das Modal zur Auswahl von GPKG-Layern an.
	 * @returns Die ausgewählten Layer oder null bei Abbruch
	 */
	private async _showGpkgLayerSelectModal(filename: string, pkg: GeoPackage, features: string[], tiles: string[]): Promise<GpkgLayerInfo[] | null> {
		// Geometrietyp für Feature-Layer ermitteln
		const getGeometryType = (tableName: string): GpkgGeometryType => {
			try {
				const featureDao = pkg.getFeatureDao(tableName);
				const geomColumn = featureDao.geometryColumns;
				if (geomColumn?.geometry_type_name) {
					// GeoPackage-Geometrietyp in unseren Typ konvertieren
					const gpkgType = geomColumn.geometry_type_name.toUpperCase();
					if (gpkgType.includes('POINT')) return gpkgType.includes('MULTI') ? 'MultiPoint' : 'Point';
					if (gpkgType.includes('LINE')) return gpkgType.includes('MULTI') ? 'MultiLineString' : 'LineString';
					if (gpkgType.includes('POLYGON')) return gpkgType.includes('MULTI') ? 'MultiPolygon' : 'Polygon';
					if (gpkgType.includes('GEOMETRY')) return 'GeometryCollection';
				}
			} catch (e) {
				console.warn(`Konnte Geometrietyp für ${tableName} nicht ermitteln:`, e);
			}
			return 'unknown';
		};

		// Autostyle anhand der Attributnamen erkennen (via Handler wenn vorhanden)
		const getAutoStyle = (tableName: string): GpkgAutoStyleType => {
			if (!this.gpkgAutoStyleHandler) {
				return null;
			}
			try {
				const featureDao = pkg.getFeatureDao(tableName);
				const iterator = featureDao.queryForAll();
				for (const row of iterator) {
					// Erstes Feature prüfen - Handler entscheidet über Style
					const keys = Object.keys(row).map(k => k.toUpperCase());
					return this.gpkgAutoStyleHandler.detectAutoStyle(keys);
				}
			} catch (e) {
				// Ignorieren
			}
			return null;
		};

		// Layer-Liste für das Modal vorbereiten
		const layers: GpkgLayerInfo[] = [
			...features.map(name => ({ name, type: 'feature' as const, geometryType: getGeometryType(name), autoStyle: getAutoStyle(name), selected: true })),
			...tiles.map(name => ({ name, type: 'tile' as const, selected: true }))
		];

		if (layers.length === 0) {
			return null;
		}

		const modalRef = this.modalService.open(GpkgLayerSelectModalComponent, {
			centered: true,
			backdrop: 'static'
		});

		modalRef.componentInstance.filename = filename;
		modalRef.componentInstance.layers = layers;

		try {
			return await modalRef.result;
		} catch {
			// Modal wurde abgebrochen
			return null;
		}
	}

	/**
	 * Verarbeitet eine ZIP-Datei mit Shapefile-Inhalt
	 */
	private async _handleShapefileZip(name: string, zipBuffer: ArrayBuffer): Promise<void> {
		try {
			const geojson = await shp(zipBuffer);

			// shpjs gibt entweder ein einzelnes FeatureCollection oder ein Array davon zurück
			const collections = Array.isArray(geojson) ? geojson : [geojson];

			// Alle Features sammeln
			const allFeatures: any[] = [];
			for (const fc of collections) {
				if (fc?.type === 'FeatureCollection' && Array.isArray(fc.features)) {
					allFeatures.push(...fc.features);
				}
			}

			if (allFeatures.length === 0) {
				console.error('Keine Features in der ZIP-Datei gefunden');
				return;
			}

			const resultGeoJson = {
				type: 'FeatureCollection' as const,
				features: allFeatures
			};

			// Als GeoJSON speichern (mit .geojson-Endung)
			const geoJsonName = name.replace(/\.zip$/i, '.geojson');
			await this.ps.setProjectValue(geoJsonName, JSON.stringify(resultGeoJson), 'attachments');
			await this.ps.addGeodataFile(geoJsonName);
		} catch (e) {
			console.error('ZIP-Shapefile-Parsing fehlgeschlagen:', e);
		}
	}

	/**
	 * Verarbeitet eine 7z-Datei mit Shapefile-Inhalt
	 */
	private async _handleShapefile7z(name: string, buffer: ArrayBuffer): Promise<void> {
		try {
			// libarchive-wasm initialisieren und Archiv öffnen
			const libarchive = await libarchiveWasm();
			const reader = new ArchiveReader(libarchive, new Int8Array(buffer));

			// Shapefile-Dateien finden (.shp, .dbf, .prj)
			let shpBuffer: ArrayBuffer | undefined;
			let dbfBuffer: ArrayBuffer | undefined;
			let prjText: string | undefined;

			// Alle Entries durchlaufen und relevante Dateien extrahieren
			let entry: ReturnType<typeof reader.nextEntry>;
			while ((entry = reader.nextEntry()) !== null) {
				const fileName = entry.getPathname();
				const lower = fileName.toLowerCase();
				const size = entry.getSize();

				if (lower.endsWith('.shp') && size > 0) {
					const data = reader.readData(size);
					if (data) {
						// Int8Array zu ArrayBuffer konvertieren
						shpBuffer = new Uint8Array(data).buffer;
					}
				} else if (lower.endsWith('.dbf') && size > 0) {
					const data = reader.readData(size);
					if (data) {
						dbfBuffer = new Uint8Array(data).buffer;
					}
				} else if (lower.endsWith('.prj') && size > 0) {
					const data = reader.readData(size);
					if (data) {
						prjText = new TextDecoder().decode(new Uint8Array(data));
					}
				} else {
					// Andere Dateien überspringen
					reader.skipData();
				}
			}

			reader.free();

			if (!shpBuffer || !dbfBuffer) {
				console.error('Keine vollständigen Shapefile-Daten in der 7z-Datei gefunden');
				return;
			}

			// Shapefile verarbeiten wie bei ZIP
			const geoJsonName = name.replace(/\.7z$/i, '.geojson');
			await this._parseAndSaveShapefile(geoJsonName, shpBuffer, dbfBuffer, prjText);

		} catch (e) {
			console.error('7z-Shapefile-Parsing fehlgeschlagen:', e);
		}
	}

	/**
	 * Verarbeitet eine .shp-Datei mit optionalen Companion-Dateien
	 */
	private async _handleShapefileWithCompanions(name: string, shpBuffer: ArrayBuffer, companionFiles?: File[], originalFileName?: string): Promise<void> {
		const baseName = (originalFileName || name).replace(/\.(shp|geojson)$/i, '').toLowerCase();

		// Companion-Dateien suchen (case-insensitive)
		let dbfBuffer: ArrayBuffer | undefined;
		let prjText: string | undefined;

		if (companionFiles) {
			for (const file of companionFiles) {
				const lowerName = file.name.toLowerCase();
				const fileBaseName = lowerName.replace(/\.(dbf|prj|shx)$/i, '');
				// Vergleiche nur den Basisnamen case-insensitive
				if (fileBaseName === baseName && lowerName.endsWith('.dbf')) {
					dbfBuffer = await this._readFileAsArrayBuffer(file);
				} else if (fileBaseName === baseName && lowerName.endsWith('.prj')) {
					prjText = await this._readFileAsText(file);
				}
			}
		}

		if (!dbfBuffer) {
			throw new Error('Keine .dbf-Datei gefunden. Bitte wählen Sie die .shp und .dbf Datei gemeinsam aus.');
		}

		await this._parseAndSaveShapefile(name, shpBuffer, dbfBuffer, prjText);
	}

	/**
	 * Parst Shapefile-Daten und speichert sie als GeoJSON
	 */
	private async _parseAndSaveShapefile(name: string, shpBuffer: ArrayBuffer, dbfBuffer: ArrayBuffer, prjText?: string): Promise<void> {
		try {
			// shapefile-Bibliothek für robustes Parsen verwenden
			const features: any[] = [];
			const source = await shapefile.open(shpBuffer, dbfBuffer);
			let result = await source.read();
			while (!result.done) {
				features.push(result.value);
				result = await source.read();
			}

			if (features.length === 0) {
				console.error('Keine Features im Shapefile gefunden');
				return;
			}

			// CRS aus PRJ-Text extrahieren und Koordinaten transformieren
			const crs = prjText ? this._extractCrsFromPrj(prjText) : null;
			if (crs && crs !== 'EPSG:4326') {
				console.log(`Shapefile CRS erkannt: ${crs}, transformiere zu WGS84...`);
				for (const feature of features) {
					if (feature.geometry) {
						this._transformGeometryToWgs84(feature.geometry, crs);
					}
				}
			}

			const geojson = {
				type: 'FeatureCollection' as const,
				features: features
			};

			// Als GeoJSON speichern (mit .geojson-Endung)
			const geoJsonName = name.replace(/\.(shp|zip)$/i, '.geojson');
			await this.ps.setProjectValue(geoJsonName, JSON.stringify(geojson), 'attachments');
			await this.ps.addGeodataFile(geoJsonName);
		} catch (e) {
			console.error('Shapefile-Parsing fehlgeschlagen:', e);
		}
	}

	/**
	 * Extrahiert EPSG-Code aus PRJ/WKT-Text
	 */
	private _extractCrsFromPrj(prjText: string): string | null {
		// Format: "EPSG:5650" oder "urn:ogc:def:crs:EPSG::5650"
		const epsgMatch = prjText.match(/EPSG::?(\d+)/i);
		if (epsgMatch) {
			return `EPSG:${epsgMatch[1]}`;
		}

		// PRJ/WKT-Format parsen - suche nach AUTHORITY["EPSG","XXXX"]
		const authorityMatch = prjText.match(/AUTHORITY\s*\[\s*"EPSG"\s*,\s*"(\d+)"\s*\]/i);
		if (authorityMatch) {
			return `EPSG:${authorityMatch[1]}`;
		}

		// Bekannte CRS-Namen aus WKT erkennen
		const wktCrsMapping: { [key: string]: string } = {
			'ETRS89_UTM_zone_32N': 'EPSG:25832',
			'ETRS89 / UTM zone 32N': 'EPSG:25832',
			'ETRS_1989_UTM_Zone_32N': 'EPSG:25832',
			'ETRS89_UTM_zone_33N': 'EPSG:25833',
			'ETRS89 / UTM zone 33N': 'EPSG:25833',
			'ETRS_1989_UTM_Zone_33N': 'EPSG:25833',
			'DHDN_3_Degree_Gauss_Zone_3': 'EPSG:31467',
			'DHDN / 3-degree Gauss-Kruger zone 3': 'EPSG:31467',
			'DHDN_3_Degree_Gauss_Zone_4': 'EPSG:31468',
			'DHDN / 3-degree Gauss-Kruger zone 4': 'EPSG:31468',
			'DHDN_3_Degree_Gauss_Zone_5': 'EPSG:31469',
			'DHDN / 3-degree Gauss-Kruger zone 5': 'EPSG:31469',
			'WGS_1984_UTM_Zone_32N': 'EPSG:32632',
			'WGS 84 / UTM zone 32N': 'EPSG:32632',
			'WGS_1984_UTM_Zone_33N': 'EPSG:32633',
			'WGS 84 / UTM zone 33N': 'EPSG:32633',
		};

		// Suche nach PROJCS["Name",...]
		const projcsMatch = prjText.match(/PROJCS\s*\[\s*"([^"]+)"/i);
		if (projcsMatch) {
			const projName = projcsMatch[1];
			if (wktCrsMapping[projName]) {
				return wktCrsMapping[projName];
			}
			// Versuche partielle Übereinstimmung
			for (const [key, value] of Object.entries(wktCrsMapping)) {
				if (projName.includes(key) || key.includes(projName)) {
					return value;
				}
			}
		}

		// Fallback: Suche nach UTM Zone in WKT
		const utmZoneMatch = prjText.match(/UTM[_\s]*[Zz]one[_\s]*(\d+)N/i);
		if (utmZoneMatch) {
			const zone = parseInt(utmZoneMatch[1]);
			if (prjText.includes('ETRS') || prjText.includes('European')) {
				return `EPSG:${25800 + zone}`;
			}
			return `EPSG:${32600 + zone}`;
		}

		// Fallback: Suche nach Gauss-Kruger Zone
		const gkZoneMatch = prjText.match(/[Gg]auss[_\s\-]*[Kk]r[uü]ger[_\s]*[Zz]one[_\s]*(\d+)/i);
		if (gkZoneMatch) {
			const zone = parseInt(gkZoneMatch[1]);
			return `EPSG:${31464 + zone}`;
		}

		console.warn('CRS konnte nicht erkannt werden:', prjText.substring(0, 200));
		return null;
	}

	/**
	 * Transformiert GeoJSON-Geometrie in-place zu WGS84
	 * Verwendet TransformationService für konsistente Ergebnisse mit project-regions.component
	 */
	private _transformGeometryToWgs84(geometry: any, sourceCrs: string): void {
		// Erste Koordinate finden für Zonenkennung-Erkennung
		const firstCoord = this._findFirstCoordinate(geometry.coordinates);
		const { zonePrefix, mappedCrs } = this._detectZonePrefixAndMapCrs(firstCoord?.[0], sourceCrs);
		const targetCrs = mappedCrs || sourceCrs;

		const transformCoord = (coord: number[]): void => {
			if (coord.length >= 2) {
				try {
					let x = coord[0];
					const y = coord[1];

					// Zonenkennung entfernen falls erkannt
					if (zonePrefix) {
						x = x - zonePrefix;
					}

					const transformed = this.ts.from({ system: targetCrs, x: x, y: y } as any);
					if (transformed && isFinite(transformed.lat) && isFinite(transformed.lng)) {
						coord[0] = transformed.lng;
						coord[1] = transformed.lat;
					}
				} catch (e) {
					console.warn('Koordinaten-Transformation fehlgeschlagen:', coord, e);
				}
			}
		};

		const transformCoords = (coords: any): void => {
			if (Array.isArray(coords)) {
				if (typeof coords[0] === 'number') {
					transformCoord(coords);
				} else {
					for (const c of coords) {
						transformCoords(c);
					}
				}
			}
		};

		if (geometry.coordinates) {
			transformCoords(geometry.coordinates);
		}
	}

	/**
	 * Findet die erste Koordinate in einer verschachtelten Koordinatenstruktur
	 */
	private _findFirstCoordinate(coords: any): number[] | null {
		if (!Array.isArray(coords)) return null;
		if (typeof coords[0] === 'number') return coords;
		return this._findFirstCoordinate(coords[0]);
	}

	/**
	 * Erkennt Zonenkennungen in X-Koordinaten und mappt CRS entsprechend.
	 * Gleiche Logik wie in project-regions.component.ts
	 */
	private _detectZonePrefixAndMapCrs(x: number | undefined, crs: string | null): { zonePrefix: number | null, mappedCrs: string | null } {
		if (!x || !crs) return { zonePrefix: null, mappedCrs: null };

		// EPSG:5650 = ETRS89/UTM zone 33N (zE-N) - hat Zonenkennung 33 im Rechtswert
		if (crs === 'EPSG:5650' && x > 33000000 && x < 34000000) {
			return { zonePrefix: 33000000, mappedCrs: 'EPSG:25833' };
		}

		// EPSG:5649 = ETRS89/UTM zone 32N (zE-N) - hat Zonenkennung 32 im Rechtswert
		if (crs === 'EPSG:5649' && x > 32000000 && x < 33000000) {
			return { zonePrefix: 32000000, mappedCrs: 'EPSG:25832' };
		}

		// EPSG:4647 = ETRS89/UTM zone 32N (zE-N) - hat Zonenkennung 32 im Rechtswert
		if (crs === 'EPSG:4647' && x > 32000000 && x < 33000000) {
			return { zonePrefix: 32000000, mappedCrs: 'EPSG:25832' };
		}

		// UTM-Zonen mit Zonenkennung in X-Koordinate
		if ((crs === 'EPSG:25832' || crs === 'EPSG:32632') && x > 32000000 && x < 33000000) {
			return { zonePrefix: 32000000, mappedCrs: null };
		}

		if ((crs === 'EPSG:25833' || crs === 'EPSG:32633') && x > 33000000 && x < 34000000) {
			return { zonePrefix: 33000000, mappedCrs: null };
		}

		return { zonePrefix: null, mappedCrs: null };
	}

	private _readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as ArrayBuffer);
			reader.onerror = () => reject(reader.error);
			reader.readAsArrayBuffer(file);
		});
	}

	private _readFileAsText(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = () => reject(reader.error);
			reader.readAsText(file);
		});
	}
	
	saveLayersToLocalStorage(mapDef: MapDefinition) {
		if (!mapDef) return;
		this.mapDef.set(mapDef);
		this.ps.setProjectValue("mapDefinition", mapDef);
		// Verwaiste hiddenLayers-Einträge bereinigen:
		// Sublayer die im mapDef enabled sind, dürfen nicht versteckt bleiben
		this._cleanupHiddenLayers(mapDef);
		this.layersChanged.emit();
	}

	/**
	 * Entfernt hiddenLayers-Einträge für WMS-Sublayer die im mapDef aktiviert sind.
	 * Verhindert, dass Layer unsichtbar bleiben wenn sie im LayerStore neu hinzugefügt werden.
	 */
	private async _cleanupHiddenLayers(mapDef: MapDefinition) {
		const hidden = await this.hiddenLayers();
		if (hidden.length === 0) return;

		// Alle aktuell aktivierten WMS-Sublayer-Titel sammeln
		const enabledTitles = new Set<string>();
		const enabledKeys = new Set<string>();
		for (const ld of mapDef.layers) {
			if (ld.type !== 'wms' || !ld.layers) continue;
			for (const ol of ld.layers) {
				if (!ol.enabled) continue;
				enabledTitles.add(ol.title || ol.name);
				enabledKeys.add(`${ld.name}_${ol.title || ol.name}`);
			}
		}

		// Einträge entfernen die jetzt enabled sind
		const cleaned = hidden.filter(h => {
			// Direkter Sublayer-Key: "BB FGK_Forstgrundkarte (Gelb)"
			if (enabledKeys.has(h)) return false;
			// Parent-Key: "BB FGK" → versteckt alle Sublayer
			const ld = mapDef.layers.find(l => l.type === 'wms' && l.name === h);
			if (ld?.layers?.some(ol => ol.enabled)) return false;
			return true;
		});

		if (cleaned.length !== hidden.length) {
			await this.ps.setProjectValue('hiddenLayers', cleaned);
		}
	}

	/**
	 * Speichert nur die Layer-Reihenfolge ohne layersChanged zu triggern
	 * Verwendet für Drag & Drop in der Layerübersicht
	 */
	saveLayerOrderOnly(mapDef: MapDefinition) {
		if (!mapDef) return;
		this.mapDef.set(mapDef);
		this.ps.setProjectValue("mapDefinition", mapDef);
	}

	/**
	 * Speichert JSON-Daten mit Speicherort-Auswahl und optionalem Teilen
	 */
	async saveJSONToFile(jsonData: string|any, filename = 'data.json'): Promise<boolean> {
		if (!jsonData) {
			console.warn(`saveJSONToFile: Keine Daten für ${filename}`);
			return false;
		}

		const jsonString = typeof jsonData === 'string'
			? jsonData
			: JSON.stringify(jsonData, null, 2);

		const mimeType = filename.endsWith('.geojson')
			? 'application/geo+json'
			: 'application/json';

		const result = await this.fileExport.saveOrShare({
			filename: filename,
			mimeType: mimeType,
			content: jsonString
		});

		return result.success;
	}

	/**
	 * Speichert mehrere JSON-Dateien gebündelt (ein Dialog statt mehrere)
	 */
	async saveMultipleJSONToFiles(files: {data: any, filename: string}[]): Promise<boolean> {
		const exportFiles = files
			.filter(f => f.data)
			.map(f => ({
				filename: f.filename,
				mimeType: f.filename.endsWith('.geojson') ? 'application/geo+json' : 'application/json',
				content: typeof f.data === 'string' ? f.data : JSON.stringify(f.data, null, 2)
			}));

		if (exportFiles.length === 0) return false;

		const result = await this.fileExport.saveMultipleFiles(exportFiles, 'Kartendaten');
		return result.success;
	}
}
